<template>
  <article class="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="ai-workspace-card"
    :data-card-type="card.type">
    <header class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
      <i class="pi pi-th-large text-xs text-gray-400 shrink-0"></i>
      <h3 class="text-sm font-medium text-gray-700 truncate flex-1" :title="title" data-testid="ai-card-title">
        {{ title }}
      </h3>
      <button type="button"
        class="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
        :aria-label="`Dismiss ${title}`"
        data-testid="ai-card-dismiss"
        @click="emit('dismiss', card.id)">
        <i class="pi pi-times text-xs"></i>
      </button>
    </header>

    <div class="p-3">
      <!-- Unknown types degrade here rather than breaking the workspace. -->
      <AiUnsupportedCard v-if="!definition" :type="card.type" />
      <component v-else :is="definition.component" v-bind="mappedProps" />
    </div>

    <!--
      Quick actions: user-triggered only, permission-filtered server-side, and
      further filtered here to keys this frontend can actually perform — an
      unrecognised key renders nothing rather than a dead button.
    -->
    <footer v-if="actions.length"
      class="flex flex-wrap gap-1.5 px-3 py-2 border-t border-gray-100 bg-gray-50/60"
      data-testid="ai-card-actions">
      <Button v-for="action in actions" :key="action.key"
        :label="action.label"
        :icon="action.icon || undefined"
        size="small"
        text
        class="p-button-sm"
        :data-testid="`ai-card-action-${action.key}`"
        :data-mode="action.mode"
        @click="runAction(action)" />
    </footer>
  </article>
</template>

<script setup lang="ts">
import AiUnsupportedCard from './AiUnsupportedCard.vue';
import { getAiCardDefinition } from './aiCardRegistry';
import { resolveAiCardActions, type AiActionOutcome, type ResolvedAiCardAction } from './aiCardActions';
import type { AiWorkspaceCardData } from '@/composables/useAiChat';

/**
 * Chrome around one model-chosen card: title + dismiss, with the registered
 * renderer inside. Renderers are lazily imported by the registry.
 */
const props = defineProps<{ card: AiWorkspaceCardData }>();

const emit = defineEmits<{
  (e: 'dismiss', id: string): void;
  /** The card performs nothing itself — the page decides how to contain it. */
  (e: 'action', outcome: AiActionOutcome): void;
}>();

const definition = computed(() => getAiCardDefinition(props.card.type));

const title = computed(
  () => props.card.title?.trim() || definition.value?.titleFallback || 'Details',
);

const mappedProps = computed(() =>
  (definition.value ? definition.value.propsMapper(props.card.props ?? {}) : {}),
);

const router = useRouter();

// Works identically for freshly-streamed and restored cards — both carry the
// same `actions` array.
const actions = computed(() =>
  resolveAiCardActions(props.card.actions, props.card.props ?? {}, router, props.card.title),
);

// Resolving may be async (looking up a slug or a student). A null outcome means
// the action couldn't be carried out honestly — do nothing rather than guess.
const runAction = async(action: ResolvedAiCardAction) => {
  const outcome = await action.resolve();
  if (outcome) emit('action', outcome);
};
</script>
